import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabase';
import { useAuth } from '../context/AuthContext';
import {
  AlertCircle,
  Building2,
  ChevronDown,
  ChevronUp,
  Download,
  RefreshCw,
  Search,
  Users,
  Wallet,
} from 'lucide-react';
import toast from 'react-hot-toast';
import {
  fetchActiveOrganizationClientsWithBalances,
  getClientBalanceValue,
  isClientSaleBalanceRelevant,
} from '../utils/clientBalances';
import {
  fetchActiveOrganizationProvidersWithBalances,
  getProviderBalanceValue,
} from '../utils/providerBalances';
import { attachStoreOrdersToSales, SALE_STORE_ORDER_SELECT } from '../utils/saleStoreOrders';
import {
  getSalePaymentTermsColumnsSupport,
  isMissingSalePaymentTermsColumnsError,
  normalizeSalePaymentTermsRow,
  SALE_PAYMENT_TERMS_SELECT_FIELDS,
  setSalePaymentTermsColumnsSupport,
} from '../utils/salesPaymentTermsSupport';

const EPSILON = 0.000001;
const DAY_MS = 1000 * 60 * 60 * 24;
const DEFAULT_BUCKETS = {
  overdue: 0,
  next7: 0,
  next30: 0,
  next60: 0,
  over60: 0,
  total: 0,
};

const CANCELLED_PURCHASE_STATUSES = new Set([
  'anulado',
  'anulada',
  'annulled',
  'cancelled',
  'cancelado',
]);

const normalizeText = (value) =>
  String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim()
    .toLowerCase();

const toDateOnly = (value) => String(value || '').slice(0, 10);

const parseDateAtMidday = (value) => {
  const dateOnly = toDateOnly(value);
  if (!dateOnly) return null;
  const parsed = new Date(`${dateOnly}T12:00:00`);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const addDaysToDateOnly = (value, days) => {
  const baseDate = parseDateAtMidday(value);
  if (!baseDate) return null;
  const nextDate = new Date(baseDate.getTime());
  nextDate.setDate(nextDate.getDate() + Math.max(0, Number(days) || 0));
  return nextDate.toISOString().slice(0, 10);
};

const getPurchasePaymentConditionDays = (purchase) => {
  const normalizedCondition = normalizeText(purchase?.condicion_pago);
  const matchedDays = normalizedCondition.match(/(\d+)/);
  return matchedDays ? Math.max(0, Number(matchedDays[1]) || 0) : 0;
};

const shouldIncludeProviderPurchase = (purchase) => {
  if (!purchase?.id || !purchase?.proveedor_id) return false;

  const totalAmount = Math.abs(Number(purchase?.total_general) || 0);
  if (totalAmount < EPSILON) return false;

  const normalizedStatus = normalizeText(purchase?.estado);
  const normalizedVoucherType = normalizeText(purchase?.tipo_comprobante);
  const normalizedCondition = normalizeText(purchase?.condicion_pago);

  if (CANCELLED_PURCHASE_STATUSES.has(normalizedStatus)) return false;
  if (normalizedVoucherType === 'pago') return false;
  if (normalizedCondition.includes('contado')) return false;

  return true;
};

const buildProviderPurchaseDocument = (purchase) => ({
  id: purchase.id,
  amount: Math.max(0, Number(purchase?.total_general) || 0),
  due_date:
    addDaysToDateOnly(
      purchase?.fecha || purchase?.created_at,
      getPurchasePaymentConditionDays(purchase),
    ) || toDateOnly(purchase?.fecha || purchase?.created_at),
  created_at: purchase?.fecha || purchase?.created_at || null,
});

const shouldIncludeClientFallbackSale = (sale) => {
  if (!isClientSaleBalanceRelevant(sale)) return false;

  const days = Math.max(0, Number(sale?.payment_condition_days) || 0);
  const dueDate = toDateOnly(sale?.payment_due_date);
  const label = normalizeText(sale?.payment_condition_label);

  return days > 0 || Boolean(dueDate) || (label && !label.includes('contado'));
};

const buildClientFallbackSaleDocument = (sale) => ({
  id: sale.id,
  amount: Math.max(0, Number(sale?.total) || 0),
  due_date:
    toDateOnly(sale?.payment_due_date) ||
    addDaysToDateOnly(sale?.created_at, Math.max(0, Number(sale?.payment_condition_days) || 0)) ||
    toDateOnly(sale?.created_at),
  created_at: sale?.created_at || null,
});

const sortDocumentsByOldest = (documents = []) =>
  [...documents].sort((left, right) => {
    const leftDate = new Date(left?.due_date || left?.created_at || 0).getTime();
    const rightDate = new Date(right?.due_date || right?.created_at || 0).getTime();

    if (leftDate !== rightDate) return leftDate - rightDate;
    return String(left?.id || '').localeCompare(String(right?.id || ''));
  });

const normalizeDocumentsToBalance = (documents = [], balanceValue = 0) => {
  const targetBalance = Math.max(0, Number(balanceValue) || 0);
  const sorted = sortDocumentsByOldest(
    (Array.isArray(documents) ? documents : [])
      .map((document) => ({
        ...document,
        amount: Math.max(0, Number(document?.amount ?? document?.pending_amount) || 0),
      }))
      .filter((document) => document.amount > EPSILON),
  );

  if (sorted.length === 0 || targetBalance <= EPSILON) return [];

  const totalDocuments = sorted.reduce((acc, document) => acc + document.amount, 0);
  let remainingCredit = Math.max(0, totalDocuments - targetBalance);

  return sorted
    .map((document) => {
      const appliedCredit = Math.min(document.amount, remainingCredit);
      remainingCredit -= appliedCredit;
      return {
        ...document,
        pending_amount: Math.max(0, document.amount - appliedCredit),
      };
    })
    .filter((document) => document.pending_amount > EPSILON);
};

const bucketDocuments = (documents = [], residualOverdue = 0, referenceDate = new Date()) => {
  const reference = parseDateAtMidday(referenceDate.toISOString()) || new Date();
  const buckets = { ...DEFAULT_BUCKETS };

  documents.forEach((document) => {
    const amount = Math.max(0, Number(document?.pending_amount ?? document?.amount) || 0);
    if (amount <= EPSILON) return;

    const dueDate = parseDateAtMidday(document?.due_date);
    if (!dueDate) {
      buckets.overdue += amount;
      buckets.total += amount;
      return;
    }

    const dayDiff = Math.floor((dueDate.getTime() - reference.getTime()) / DAY_MS);

    if (dayDiff < 0) buckets.overdue += amount;
    else if (dayDiff <= 7) buckets.next7 += amount;
    else if (dayDiff <= 30) buckets.next30 += amount;
    else if (dayDiff <= 60) buckets.next60 += amount;
    else buckets.over60 += amount;

    buckets.total += amount;
  });

  if (residualOverdue > EPSILON) {
    buckets.overdue += residualOverdue;
    buckets.total += residualOverdue;
  }

  return buckets;
};

const formatCurrency = (value) =>
  `$ ${Number(value || 0).toLocaleString('es-AR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const sumColumn = (rows, key) =>
  rows.reduce((acc, row) => acc + Number(row?.[key] || 0), 0);

async function fetchClientSalesWithTerms({ organizationId }) {
  // Embebemos store_orders para activar el fast-path de attachStoreOrdersToSales
  // y evitar el refetch en chunks que disparaba el N+1 (ref IMPULZA-3).
  const selectBase =
    `id, client_id, total, status, active, invoice_status, billing_source, created_at, store_orders!store_orders_sale_id_fkey(${SALE_STORE_ORDER_SELECT})`;
  const selectWithTerms = `${selectBase}, ${SALE_PAYMENT_TERMS_SELECT_FIELDS}`;

  const buildQuery = (selectClause) =>
    supabase
      .from('sales')
      .select(selectClause)
      .eq('organization_id', organizationId);

  const support = getSalePaymentTermsColumnsSupport();

  if (support !== false) {
    const response = await buildQuery(selectWithTerms);

    if (!response.error) {
      setSalePaymentTermsColumnsSupport(true);
      return attachStoreOrdersToSales({
        sales: (response.data || []).map(normalizeSalePaymentTermsRow),
        organizationId,
        supabaseClient: supabase,
      });
    }

    if (!isMissingSalePaymentTermsColumnsError(response.error)) {
      return response;
    }

    setSalePaymentTermsColumnsSupport(false);
  }

  const fallbackResponse = await buildQuery(selectBase);
  if (fallbackResponse.error) return fallbackResponse;

  return attachStoreOrdersToSales({
    sales: (fallbackResponse.data || []).map(normalizeSalePaymentTermsRow),
    organizationId,
    supabaseClient: supabase,
  });
}

export default function ReportesCuentasCorrientes() {
  const navigate = useNavigate();
  const { profile } = useAuth();

  const [activeTab, setActiveTab] = useState('clientes');
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [clientesRows, setClientesRows] = useState([]);
  const [proveedoresRows, setProveedoresRows] = useState([]);
  const [sortConfig, setSortConfig] = useState({ key: 'total', direction: 'desc' });

  useEffect(() => {
    if (!profile?.organization_id) return;
    cargarDatos();
  }, [profile?.organization_id]);

  const cargarDatos = async ({ silent = false } = {}) => {
    if (silent) setRefreshing(true);
    else setLoading(true);
    try {
      const organizationId = profile.organization_id;

      const [
        clientsResponse,
        providersResponse,
        receivablesResponse,
        purchasesResponse,
        salesResponse,
      ] = await Promise.all([
        fetchActiveOrganizationClientsWithBalances({
          organizationId,
          supabaseClient: supabase,
        }),
        fetchActiveOrganizationProvidersWithBalances({
          organizationId,
          supabaseClient: supabase,
        }),
        supabase
          .from('sales_receivables')
          .select('id, client_id, due_date, pending_amount, created_at')
          .eq('organization_id', organizationId)
          .eq('is_active', true)
          .gt('pending_amount', 0),
        supabase
          .from('compras')
          .select('id, proveedor_id, proveedor_nombre, total_general, fecha, estado, condicion_pago, tipo_comprobante, created_at')
          .eq('organization_id', organizationId),
        fetchClientSalesWithTerms({ organizationId }),
      ]);

      if (clientsResponse.error) throw clientsResponse.error;
      if (providersResponse.error) throw providersResponse.error;
      if (receivablesResponse.error) throw receivablesResponse.error;
      if (purchasesResponse.error) throw purchasesResponse.error;
      if (salesResponse.error) throw salesResponse.error;

      const receivablesByClient = new Map();
      (receivablesResponse.data || []).forEach((receivable) => {
        if (!receivable?.client_id) return;
        const rows = receivablesByClient.get(receivable.client_id) || [];
        rows.push({
          id: receivable.id,
          amount: Math.max(0, Number(receivable.pending_amount) || 0),
          due_date: toDateOnly(receivable.due_date),
          created_at: receivable.created_at || null,
        });
        receivablesByClient.set(receivable.client_id, rows);
      });

      const fallbackSalesByClient = new Map();
      (salesResponse.data || [])
        .filter(shouldIncludeClientFallbackSale)
        .forEach((sale) => {
          if (!sale?.client_id) return;
          const rows = fallbackSalesByClient.get(sale.client_id) || [];
          rows.push(buildClientFallbackSaleDocument(sale));
          fallbackSalesByClient.set(sale.client_id, rows);
        });

      const purchaseDocsByProvider = new Map();
      (purchasesResponse.data || [])
        .filter(shouldIncludeProviderPurchase)
        .forEach((purchase) => {
          if (!purchase?.proveedor_id) return;
          const rows = purchaseDocsByProvider.get(purchase.proveedor_id) || [];
          rows.push(buildProviderPurchaseDocument(purchase));
          purchaseDocsByProvider.set(purchase.proveedor_id, rows);
        });

      const nextClientRows = (clientsResponse.data || [])
        .map((client) => {
          const balance = Math.max(0, getClientBalanceValue(client));
          if (balance <= EPSILON) return null;

          const baseDocuments =
            receivablesByClient.get(client.id) ||
            fallbackSalesByClient.get(client.id) ||
            [];
          const normalizedDocuments = normalizeDocumentsToBalance(baseDocuments, balance);
          const documentsTotal = normalizedDocuments.reduce(
            (acc, document) => acc + Number(document.pending_amount || 0),
            0,
          );
          const residualOverdue = Math.max(0, balance - documentsTotal);
          const buckets = bucketDocuments(normalizedDocuments, residualOverdue);

          return {
            id: client.id,
            nombre: client.full_name || 'Cliente sin nombre',
            balance,
            detailPath: `/sistema/clientes-detalle/${client.id}`,
            sourceCount: normalizedDocuments.length + (residualOverdue > EPSILON ? 1 : 0),
            ...buckets,
          };
        })
        .filter((row) => row && row.total > EPSILON)
        .sort((left, right) => right.total - left.total);

      const nextProviderRows = (providersResponse.data || [])
        .map((provider) => {
          const balance = Math.max(0, getProviderBalanceValue(provider));
          if (balance <= EPSILON) return null;

          const baseDocuments = purchaseDocsByProvider.get(provider.id) || [];
          const normalizedDocuments = normalizeDocumentsToBalance(baseDocuments, balance);
          const documentsTotal = normalizedDocuments.reduce(
            (acc, document) => acc + Number(document.pending_amount || 0),
            0,
          );
          const residualOverdue = Math.max(0, balance - documentsTotal);
          const buckets = bucketDocuments(normalizedDocuments, residualOverdue);

          return {
            id: provider.id,
            nombre: provider.razon_social || 'Proveedor sin nombre',
            balance,
            detailPath: `/sistema/proveedores-detalle/${provider.id}`,
            sourceCount: normalizedDocuments.length + (residualOverdue > EPSILON ? 1 : 0),
            ...buckets,
          };
        })
        .filter((row) => row && row.total > EPSILON)
        .sort((left, right) => right.total - left.total);

      setClientesRows(nextClientRows);
      setProveedoresRows(nextProviderRows);
    } catch (error) {
      console.error('Error cargando reporte de cuentas corrientes:', error);
      toast.error('No se pudo cargar el reporte de cuentas corrientes.');
      setClientesRows([]);
      setProveedoresRows([]);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const activeRows = activeTab === 'clientes' ? clientesRows : proveedoresRows;

  const filteredRows = useMemo(() => {
    const needle = normalizeText(searchTerm);
    let rows = !needle ? activeRows : activeRows.filter((row) => normalizeText(row.nombre).includes(needle));

    rows = [...rows].sort((a, b) => {
      if (sortConfig.key === 'nombre') {
        const cmp = normalizeText(a.nombre).localeCompare(normalizeText(b.nombre));
        return sortConfig.direction === 'desc' ? -cmp : cmp;
      }
      const av = Number(a?.[sortConfig.key] || 0);
      const bv = Number(b?.[sortConfig.key] || 0);
      return sortConfig.direction === 'desc' ? bv - av : av - bv;
    });

    return rows;
  }, [activeRows, searchTerm, sortConfig]);

  const toggleSort = (key) => {
    setSortConfig((prev) => {
      if (prev.key !== key) return { key, direction: key === 'nombre' ? 'asc' : 'desc' };
      return { key, direction: prev.direction === 'desc' ? 'asc' : 'desc' };
    });
  };

  const sortIcon = (key) => {
    if (sortConfig.key !== key) return null;
    return sortConfig.direction === 'desc' ? <ChevronDown size={11} className="inline ml-1" /> : <ChevronUp size={11} className="inline ml-1" />;
  };

  const exportCSV = () => {
    if (filteredRows.length === 0) {
      toast.error('No hay filas para exportar');
      return;
    }
    const header = [
      activeTab === 'clientes' ? 'Cliente' : 'Proveedor',
      'Vencida',
      'Proximos 7 dias',
      'Hasta 30 dias',
      'Hasta 60 dias',
      'Mas de 60',
      'Total',
    ];
    const escape = (val) => {
      const str = String(val ?? '');
      return /[",\n]/.test(str) ? `"${str.replace(/"/g, '""')}"` : str;
    };
    const lines = [header.join(',')];
    filteredRows.forEach((row) => {
      lines.push(
        [row.nombre, row.overdue, row.next7, row.next30, row.next60, row.over60, row.total]
          .map((v, i) => (i === 0 ? escape(v) : Number(v || 0).toFixed(2)))
          .join(','),
      );
    });
    lines.push(
      [
        'Total',
        totals.overdue.toFixed(2),
        totals.next7.toFixed(2),
        totals.next30.toFixed(2),
        totals.next60.toFixed(2),
        totals.over60.toFixed(2),
        totals.total.toFixed(2),
      ].join(','),
    );
    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const stamp = new Date().toISOString().slice(0, 10);
    a.download = `cuentas_corrientes_${activeTab}_${stamp}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Reporte exportado');
  };

  const totals = useMemo(
    () => ({
      overdue: sumColumn(filteredRows, 'overdue'),
      next7: sumColumn(filteredRows, 'next7'),
      next30: sumColumn(filteredRows, 'next30'),
      next60: sumColumn(filteredRows, 'next60'),
      over60: sumColumn(filteredRows, 'over60'),
      total: sumColumn(filteredRows, 'total'),
    }),
    [filteredRows],
  );

  const handleOpenDetail = (row) => {
    if (!row?.detailPath) return;
    navigate(row.detailPath);
  };

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6 animate-in fade-in">
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-black text-slate-800 tracking-tight flex items-center gap-2">
            <Wallet className="text-[#51A7FD]" size={22} />
            Cuentas Corrientes
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Saldos pendientes agrupados por vencimiento.
          </p>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={() => cargarDatos({ silent: true })}
            disabled={loading || refreshing}
            className="p-2.5 rounded-2xl bg-white border border-slate-200 text-slate-500 hover:text-slate-800 hover:shadow-md transition-all disabled:opacity-50"
            title="Refrescar"
            aria-label="Refrescar"
          >
            <RefreshCw size={16} className={refreshing ? 'animate-spin' : ''} />
          </button>

          <button
            onClick={exportCSV}
            disabled={loading || filteredRows.length === 0}
            className="px-4 py-2.5 rounded-2xl bg-white border border-slate-200 text-slate-600 hover:text-slate-800 hover:shadow-md transition-all text-[10px] font-black uppercase tracking-widest flex items-center gap-2 disabled:opacity-50"
            title="Exportar CSV"
          >
            <Download size={14} /> CSV
          </button>

          <div className="bg-white border border-slate-200 rounded-2xl p-1.5 flex gap-1 shadow-sm">
            <button
              onClick={() => setActiveTab('clientes')}
              className={`px-5 py-2.5 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all ${
                activeTab === 'clientes'
                  ? 'bg-blue-50 text-blue-700 shadow-sm'
                  : 'text-slate-500 hover:bg-slate-50'
              }`}
            >
              <Users size={14} className="inline mr-2" />
              Clientes
            </button>
            <button
              onClick={() => setActiveTab('proveedores')}
              className={`px-5 py-2.5 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all ${
                activeTab === 'proveedores'
                  ? 'bg-orange-50 text-orange-700 shadow-sm'
                  : 'text-slate-500 hover:bg-slate-50'
              }`}
            >
              <Building2 size={14} className="inline mr-2" />
              Proveedores
            </button>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-[2rem] border border-slate-200 shadow-sm overflow-hidden">
        <div className="p-4 border-b border-slate-100 flex flex-col lg:flex-row gap-3 lg:items-center lg:justify-between">
          <div className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 w-full lg:w-80">
            <Search size={16} className="text-slate-400" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder={`Buscar ${activeTab === 'clientes' ? 'cliente' : 'proveedor'}...`}
              className="bg-transparent border-none outline-none text-sm w-full"
            />
          </div>

          <div className="text-right">
            <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">
              Total pendiente
            </p>
            <p className="text-2xl font-black text-slate-800">
              {formatCurrency(totals.total)}
            </p>
          </div>
        </div>

        {loading ? (
          <div className="p-4 space-y-2">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="h-12 bg-slate-50 rounded-xl animate-pulse" />
            ))}
          </div>
        ) : filteredRows.length === 0 ? (
          <div className="py-20 text-center flex flex-col items-center text-slate-400">
            <div className="w-14 h-14 bg-slate-50 rounded-2xl flex items-center justify-center mb-3">
              <AlertCircle size={26} className="opacity-60" />
            </div>
            <p className="text-[11px] font-black uppercase tracking-widest text-slate-500">
              {searchTerm ? 'Sin resultados' : 'No hay saldos pendientes'}
            </p>
            <p className="text-[10px] font-medium mt-1 max-w-xs">
              {searchTerm ? 'Ajustá la búsqueda para encontrar otras cuentas.' : 'Las cuentas con saldo en cero no aparecen en este reporte.'}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto max-h-[600px] overflow-y-auto">
            <table className="w-full min-w-[980px] text-left">
              <thead className="sticky top-0 z-10">
                <tr className="bg-slate-50 text-slate-500 text-[11px] uppercase tracking-wider border-y border-slate-200">
                  <th
                    onClick={() => toggleSort('nombre')}
                    className="p-4 font-black cursor-pointer hover:text-slate-700 select-none"
                  >
                    {activeTab === 'clientes' ? 'Cliente' : 'Proveedor'} {sortIcon('nombre')}
                  </th>
                  <th
                    onClick={() => toggleSort('overdue')}
                    className="p-4 font-black text-right text-rose-600 cursor-pointer hover:opacity-80 select-none"
                  >
                    Deuda vencida {sortIcon('overdue')}
                  </th>
                  <th
                    onClick={() => toggleSort('next7')}
                    className="p-4 font-black text-right text-amber-600 cursor-pointer hover:opacity-80 select-none"
                  >
                    Próximos 7 días {sortIcon('next7')}
                  </th>
                  <th
                    onClick={() => toggleSort('next30')}
                    className="p-4 font-black text-right cursor-pointer hover:text-slate-700 select-none"
                  >
                    Hasta 30 días {sortIcon('next30')}
                  </th>
                  <th
                    onClick={() => toggleSort('next60')}
                    className="p-4 font-black text-right cursor-pointer hover:text-slate-700 select-none"
                  >
                    Hasta 60 días {sortIcon('next60')}
                  </th>
                  <th
                    onClick={() => toggleSort('over60')}
                    className="p-4 font-black text-right cursor-pointer hover:text-slate-700 select-none"
                  >
                    Más de 60 {sortIcon('over60')}
                  </th>
                  <th
                    onClick={() => toggleSort('total')}
                    className="p-4 font-black text-right bg-slate-100 cursor-pointer hover:text-slate-700 select-none"
                  >
                    Total {sortIcon('total')}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredRows.map((row) => {
                  const overduePct = row.total > EPSILON ? (row.overdue / row.total) * 100 : 0;
                  const hoverClass =
                    activeTab === 'clientes' ? 'hover:bg-blue-50/40' : 'hover:bg-orange-50/40';
                  return (
                    <tr
                      key={row.id}
                      onClick={() => handleOpenDetail(row)}
                      className={`cursor-pointer transition-colors ${hoverClass}`}
                    >
                      <td className="p-4">
                        <div className="font-black text-slate-800 uppercase tracking-tight">
                          {row.nombre}
                        </div>
                        <div className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mt-1 flex items-center gap-2">
                          {row.sourceCount} referencia{row.sourceCount === 1 ? '' : 's'}
                          {overduePct > 0.5 && (
                            <span className={`px-1.5 py-0.5 rounded-md text-[9px] font-black ${overduePct >= 70 ? 'bg-rose-100 text-rose-700' : 'bg-amber-100 text-amber-700'}`}>
                              {overduePct.toLocaleString('es-AR', { maximumFractionDigits: 0 })}% vencida
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="p-4 text-right font-bold text-rose-600">{formatCurrency(row.overdue)}</td>
                      <td className="p-4 text-right font-bold text-amber-600">{formatCurrency(row.next7)}</td>
                      <td className="p-4 text-right font-bold text-slate-700">{formatCurrency(row.next30)}</td>
                      <td className="p-4 text-right font-bold text-slate-700">{formatCurrency(row.next60)}</td>
                      <td className="p-4 text-right font-bold text-slate-700">{formatCurrency(row.over60)}</td>
                      <td className="p-4 text-right font-black text-slate-900 bg-slate-50/60">
                        {formatCurrency(row.total)}
                      </td>
                    </tr>
                  );
                })}

                <tr className="bg-slate-50/90 border-t-2 border-slate-200 sticky bottom-0">
                  <td className="p-4 font-black uppercase tracking-widest text-slate-700">Total</td>
                  <td className="p-4 text-right font-black text-rose-600">{formatCurrency(totals.overdue)}</td>
                  <td className="p-4 text-right font-black text-amber-600">{formatCurrency(totals.next7)}</td>
                  <td className="p-4 text-right font-black text-slate-800">{formatCurrency(totals.next30)}</td>
                  <td className="p-4 text-right font-black text-slate-800">{formatCurrency(totals.next60)}</td>
                  <td className="p-4 text-right font-black text-slate-800">{formatCurrency(totals.over60)}</td>
                  <td className="p-4 text-right font-black text-slate-900 bg-slate-100">{formatCurrency(totals.total)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
